/**
 * chatPanelStore
 *
 * Tiny module-level observable that mirrors whether the app's single
 * GlobalChatSystem panel is currently expanded (open).
 *
 * Why a module store instead of a React context: the chat panel owns its
 * open/closed state INTERNALLY (GlobalChatSystem calls useGlobalChat and no
 * app-level GlobalChatProvider is mounted), yet the app-level
 * GlobalLowBalanceBanner — a *sibling* of GlobalChatSystem, not a descendant —
 * needs to read that state to avoid double-warning the user.
 *
 * GlobalChatSystem publishes via setChatPanelOpen(isChatOpen) in an effect;
 * GlobalLowBalanceBanner subscribes via the useChatPanelOpen() hook.
 */

import { useSyncExternalStore } from 'react';

let chatPanelOpen = false;
const listeners = new Set<() => void>();

/** Publish the current chat-panel open state. No-op if unchanged. */
export function setChatPanelOpen(open: boolean): void {
  if (open === chatPanelOpen) return;
  chatPanelOpen = open;
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a broken listener must not break the publish loop */
    }
  });
}

/** Current snapshot — also used as getServerSnapshot (SSR-safe: always false). */
export function getChatPanelOpen(): boolean {
  return chatPanelOpen;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook: re-renders the caller whenever the chat panel opens/closes. */
export function useChatPanelOpen(): boolean {
  return useSyncExternalStore(subscribe, getChatPanelOpen, getChatPanelOpen);
}

/* ── Fullscreen ─────────────────────────────────────────────────────────────
 * The "extended chat view" is a position:fixed top:0 full-viewport takeover
 * at CHAT_FULLSCREEN_Z_INDEX, so it COVERS the in-flow banner band — the
 * band was simply invisible there (owner report 2026-08-31: the extended
 * chat view "needs a banner at the very very top of the page, not within
 * the chat interface").
 *
 * GlobalBannerCenter subscribes to this to pin the band above the takeover
 * for exactly as long as it is up. Published on the same bus and by the same
 * publisher as the open flag above; deliberately NOT folded into the
 * `data-prest0-chat-mode` attribute, whose "docked" contract is pinned by
 * chatModeSignal.test.tsx down to the literal call-site source text.
 */
let chatPanelFullScreen = false;

/** Publish whether the chat panel is currently the fullscreen takeover. */
export function setChatPanelFullScreen(fullScreen: boolean): void {
  if (fullScreen === chatPanelFullScreen) return;
  chatPanelFullScreen = fullScreen;
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a broken listener must not break the publish loop */
    }
  });
}

/** Current snapshot — also used as getServerSnapshot (SSR-safe: always false). */
export function getChatPanelFullScreen(): boolean {
  return chatPanelFullScreen;
}

/** React hook: re-renders the caller whenever the chat enters/leaves fullscreen. */
export function useChatPanelFullScreen(): boolean {
  return useSyncExternalStore(subscribe, getChatPanelFullScreen, getChatPanelFullScreen);
}
